import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { EmbedProvider } from '@/hooks/useEmbedContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useRouteSync } from '@/hooks/useRouteSync';
import { Layout } from '@/components/Layout';
import { ProjectsHub } from '@/pages/ProjectsHub';
import { ProjectDetail } from '@/pages/ProjectDetail';
import { NewProjectWizard } from '@/pages/NewProjectWizard';
import { ServiceList } from '@/pages/ServiceList';
import { ServiceDetail } from '@/pages/ServiceDetail';
import { DesignSystemEditor } from '@/pages/DesignSystemEditor';
import { DesignSystemPreview } from '@/pages/DesignSystemPreview';

/** Syncs child app route with parent shell URL for deep linking */
function RouteSync() {
  useRouteSync();
  return null;
}

/** Redirect helper for old /redesigns/:id URLs */
function RedesignRedirect() {
  const { id } = useParams();
  return <Navigate to={`/ui-orchestrator/projects/redesign-${id}`} replace />;
}

export function App() {
  return (
    <EmbedProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <RouteSync />
            <Routes>
              {/* Chromeless preview page for Figma capture — no auth, no Layout */}
              <Route path="/ui-orchestrator/design-system-preview/:serviceProfileId" element={<DesignSystemPreview />} />

              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                {/* Projects */}
                <Route path="/ui-orchestrator" element={<ProjectsHub />} />
                <Route path="/ui-orchestrator/projects/new" element={<NewProjectWizard />} />
                <Route path="/ui-orchestrator/projects/:projectId" element={<ProjectDetail />} />

                {/* Services */}
                <Route path="/ui-orchestrator/services" element={<ServiceList />} />
                <Route path="/ui-orchestrator/services/:id/design-system" element={<DesignSystemEditor />} />
                <Route path="/ui-orchestrator/services/:id" element={<ServiceDetail />} />

                {/* Old URL redirects */}
                <Route path="/ui-orchestrator/packs/:id" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/redesigns" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/redesigns/:id" element={<RedesignRedirect />} />
                <Route path="/ui-orchestrator/voice" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/jobs" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/jobs/:id" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/audit" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/metrics" element={<Navigate to="/ui-orchestrator/" replace />} />
                <Route path="/ui-orchestrator/components" element={<Navigate to="/ui-orchestrator/" replace />} />

                {/* Catch-all */}
                <Route path="*" element={<ProjectsHub />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </AuthProvider>
    </EmbedProvider>
  );
}
